import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import ApplicationAdd from "../../components/common/ApplicationAdd";
import ApplicationStep2 from "../../components/common/ApplicationStep2";
import ApplicationStep3 from "../../components/common/ApplicationStep3";
import ApplicationStep4 from "../../components/common/ApplicationStep4";
import ApplicationStep5 from "../../components/common/ApplicationStep5";
import ApplicationMetadata from "../../components/common/ApplicationMetadata";
import OperatorsPanel from "../../components/common/OperatorsPanel";
import Stepper from "../../components/common/Stepper";
import {
  getApplicationContent,
  getApplicationMetadata,
  getOpenCommentCounts,
  getAnsweredCommentCounts,
} from "../../services/Application";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { useAuth } from "../../context/AuthContext";
import ConfirmPopup from "../../components/common/ConfirmPopup";
import AccessControl from "../../components/common/AccessControl";
import OperatorRemarksCard from "../../components/common/OperatorRemarksCard";
import config from "../../config";
import { getOpenOperatorRemarks } from "../../utils/operatorRemarks";
import logger from "../../utils/logger";
function ProcessApplication() {
  const { accessToken, checkPermission } = useAuth();
  const accessTokenRef = useRef(accessToken);
  const hasAccessToken = Boolean(accessToken);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [conversationId, setConversationId] = useState();
  const [conversationData, setConversationData] = useState();
  const [status, setStatus] = useState(1);
  const [trigger, setTrigger] = useState(0);
  const [callbackRegister, setCallbackRegister] = useState({});
  const [operator, setOperator] = useState();
  const [displayMode, setDisplayMode] = useState(1); // 0 = Application, 1 = Metadata
  const [warnings, setWarnings] = useState([0, 0, 0, 0, 0]);
  const [messages, setMessages] = useState([0, 0, 0, 0, 0]);
  const [answeredMessages, setAnsweredMessages] = useState([0, 0, 0, 0, 0]); // Nowy state
  const [operatorRemarks, setOperatorRemarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const containerRef = useRef(null);
  const autoRedirectDoneRef = useRef(false);
  const displayModeRef = useRef(displayMode);

  const applicationStepTitles = [
    "WNIOSKODAWCA",
    "EKSPORTOWANY PRODUKT / TOWAR",
    "PROCES PRODUKCJI / ODBIORCA TOWARU",
    "ZAŁĄCZNIKI",
    "PODSUMOWANIE / OŚWIADCZENIA",
  ];

  /**
   * Array of metadata step titles.
   * @type {string[]}
   */
  const metadataStepTitles = [
    "WYPEŁNIANY",
    "ROZPATRYWANY",
    "OPŁATA I PODPIS",
    "W AKCEPTACJI",
    "ŚWIADECTWO WYSTAWIONE",
  ];

  const metadataStepTitlesCancelled = [
    "WYPEŁNIANY",
    "ROZPATRYWANY",
    "OPŁATA I PODPIS",
    "W AKCEPTACJI",
    "SPRAWA ANULOWANA",
  ];

  const statusToModeMap = {
    1: 0,
    2: 1,
    3: 1,
    4: 1,
    5: 1,
    6: 1,
    7: 1,
    8: 1,
    9: 1,
    10: 1,
    11: 1,
    12: 1,
    13: 1,
    14: 1,
    15: 1,
    16: 1,
    17: 1,
    18: 1,
    19: 1,
  };

  const statusToActiveStepMap = {
    1: 1,
    2: 1,
    3: 2,
    4: 1,
    5: 3,
    6: 3,
    7: 2,
    8: 5,
    9: 3,
    10: 3,
    11: 3,
    12: 3,
    13: 4,
    14: 4,
    15: 5,
    16: 5,
    17: 5,
    18: 5,
    19: 5,
  };

  let { step, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const forcedView = searchParams.get("view");
  const debug = config.runmode.debug;

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  const handleResize = () => {
    if (window.innerWidth >= 992) {
      setIsSidebarVisible(true);
    } else {
      setIsSidebarVisible(false);
    }
  };

  const handleDisplayModeChange = useCallback(
    (mode) => {
      setDisplayMode(mode);

      const params = new URLSearchParams(location.search || "");
      const nextView = mode === 0 ? "application" : "metadata";
      params.set("view", nextView);
      if (mode !== 0) {
        params.delete("focus");
      }

      const nextSearch = params.toString();
      const currentSearch = (location.search || "").replace(/^\?/, "");
      if (nextSearch === currentSearch) {
        return;
      }

      navigate(
        `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}${location.hash || ""}`,
        { replace: true }
      );
    },
    [location.hash, location.pathname, location.search, navigate]
  );

  const handleRemarkClick = useCallback(
    (remark) => {
      const params = new URLSearchParams(location.search || "");
      params.set("view", "application");
      params.set("comment", remark.id);
      params.set("focusRequest", String(Date.now()));
      params.delete("focus");

      navigate(
        `/application/edit/${remark.step}/${id}?${params.toString()}`,
      );
    },
    [id, location.search, navigate]
  );

  useEffect(() => {
    autoRedirectDoneRef.current = false;
  }, [id]);

  const reloadMessagesCount = useCallback(async () => {
    const token = accessTokenRef.current;
    if (!token) return;

    const [msgCounts, answeredCounts, applicationContent] = await Promise.all([
      getOpenCommentCounts(id, token),
      getAnsweredCommentCounts(id, token),
      getApplicationContent(id, token),
    ]);
    setMessages(msgCounts);
    setAnsweredMessages(answeredCounts);
    setOperatorRemarks(getOpenOperatorRemarks(applicationContent));
  }, [id]);

  const registerCallback = useCallback(
    (callbackId, callback) => {
      const wrappedCallback = (newStatus) => {
        const update = async (status) => {
          await callback(status);

          if (
            status === "open" ||
            status === "solved" ||
            status === "answered"
          ) {
            reloadMessagesCount();
          }
        };

        update(newStatus);
      };

      setCallbackRegister((prevState) => ({
        ...prevState,
        [callbackId]: wrappedCallback,
      }));

      return wrappedCallback;
    },
    [reloadMessagesCount]
  );

  const setAllCommunicationSpotsInactive = () => {
    Object.values(callbackRegister).forEach((callback) => {
      callback("inactive");
    });
  };

  const setWarningCount = async (index, count) => {
    if (debug) logger.log("Warning count [" + index + "]: " + count);
    setWarnings((prevWarnings) => {
      const newWarnings = [...prevWarnings];
      newWarnings[index] = count;
      return newWarnings;
    });
  };

  useEffect(() => {
    const resetScrollableElement = (element) => {
      if (!element) return;
      element.scrollTop = 0;
      element.scrollLeft = 0;
      if (typeof element.scrollTo === "function") {
        element.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
    };

    const resetScrollPosition = () => {
      resetScrollableElement(containerRef.current);

      if (typeof document !== "undefined") {
        document
          .querySelectorAll(".content, .main-content")
          .forEach((element) => resetScrollableElement(element));
      }

      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
    };

    resetScrollPosition();
    const rafId =
      typeof window !== "undefined"
        ? window.requestAnimationFrame(resetScrollPosition)
        : null;

    return () => {
      if (rafId !== null && typeof window !== "undefined") {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [step, displayMode]);

  useEffect(() => {
    if (debug) logger.log("trigger changed [" + trigger + "]");
  }, [trigger]);

  useEffect(() => {
    if (debug) logger.log("displayMode changed [" + displayMode + "]");
    displayModeRef.current = displayMode;
  }, [displayMode]);

  useEffect(() => {
    if (debug) logger.log("warnings changed [" + warnings[0] + "]");
  }, [warnings]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const token = accessTokenRef.current;
      if (!token) {
        if (isMounted) {
          setLoading(false);
        }
        return;
      }

      if (isMounted) {
        setLoading(true);
      }

      if (id === "00000000-0000-0000-0000-000000000000") {
        if (isMounted) {
          setDisplayMode(0);
          setLoading(false);
        }
        return;
      }

      try {
        const metadata = await getApplicationMetadata(id, token);
        let normalizedStatus = null;
        if (metadata) {
          const statusId = Number(metadata.status);
          normalizedStatus = Number.isNaN(statusId)
            ? metadata.status
            : statusId;
          if (isMounted) {
            setStatus(metadata.status);
          }
          let nextDisplayMode = statusToModeMap[normalizedStatus];
          const isClient =
            !checkPermission("Obsługa wniosków") &&
            !checkPermission("Obsługa wysyłki");
          if (normalizedStatus === 4 && isClient && !forcedView) {
            nextDisplayMode = 0;
          }
          if (forcedView === "application") {
            nextDisplayMode = 0;
          } else if (forcedView === "metadata") {
            nextDisplayMode = 1;
          } else if (displayModeRef.current === 0) {
            nextDisplayMode = 0;
          }
          if (isMounted) {
            setDisplayMode(nextDisplayMode);
            setOperator(metadata.Operator);
          }
        }

        const [msgCounts, answeredCounts, applicationContent] =
          await Promise.all([
            getOpenCommentCounts(id, token),
            getAnsweredCommentCounts(id, token),
            getApplicationContent(id, token),
          ]);
        if (isMounted) {
          setMessages(msgCounts);
          setAnsweredMessages(answeredCounts);
          setOperatorRemarks(getOpenOperatorRemarks(applicationContent));
        }

        const isClient =
          !checkPermission("Obsługa wniosków") &&
          !checkPermission("Obsługa wysyłki");
        const firstWithMessage = Array.isArray(msgCounts)
          ? msgCounts.findIndex((count) => count > 0)
          : -1;
        const targetStep = firstWithMessage >= 0 ? firstWithMessage + 1 : null;
        const autoRedirectKey = `autoredirect_step_${id}`;
        const lastRedirectStep =
          typeof window !== "undefined"
            ? sessionStorage.getItem(autoRedirectKey)
            : null;
        if (
          normalizedStatus === 4 &&
          isClient &&
          !forcedView &&
          targetStep != null &&
          String(targetStep) !== String(lastRedirectStep || "") &&
          !autoRedirectDoneRef.current
        ) {
          const nextParams = new URLSearchParams(location.search);
          nextParams.set("view", "application");
          nextParams.set("focus", "comments");
          autoRedirectDoneRef.current = true;
          if (typeof window !== "undefined") {
            sessionStorage.setItem(autoRedirectKey, String(targetStep));
          }
          navigate(
            `/application/edit/${targetStep}/${id}?${nextParams.toString()}`,
            { replace: true }
          );
          return;
        }
        autoRedirectDoneRef.current = true;
      } catch (error) {
        logger.error("Failed to fetch application metadata:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      isMounted = false;
      window.removeEventListener("resize", handleResize);
    };
  }, [id, hasAccessToken, forcedView]);

  useEffect(() => {
    if (displayMode !== 0) return;
    if (id === "00000000-0000-0000-0000-000000000000") return;

    const isClient =
      !checkPermission("Obsługa wniosków") &&
      !checkPermission("Obsługa wysyłki");
    if (!isClient) return;
    if (Number(status) !== 4) return;

    const stepIndex = Number(step) - 1;
    if (!Number.isInteger(stepIndex) || stepIndex < 0) return;

    const openMessagesOnCurrentStep = messages[stepIndex] || 0;
    const shouldFocusComments = openMessagesOnCurrentStep > 0;

    const params = new URLSearchParams(location.search);
    const hasFocusComments = params.get("focus") === "comments";
    if (shouldFocusComments === hasFocusComments) return;

    if (shouldFocusComments) {
      params.set("focus", "comments");
    } else {
      params.delete("focus");
    }

    const nextSearch = params.toString();
    navigate(
      `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}${location.hash || ""}`,
      { replace: true }
    );
  }, [
    checkPermission,
    displayMode,
    id,
    location.hash,
    location.pathname,
    location.search,
    messages,
    navigate,
    status,
    step,
  ]);

  // Focus scrolling removed by request.

  // useEffect(() => {
  //   setLoading(true);
  //   const fetchData = async () => {
  //     try {
  //       const msgCounts = await getOpenCommentCounts(id, accessToken);
  //       const answeredCounts = await getAnsweredCommentCounts(id, accessToken);
  //       setMessages(msgCounts);
  //       setAnsweredMessages(answeredCounts);
  //       if (debug) {
  //         logger.log("Messages count - step: " + msgCounts);onNext
  //         logger.log("Answered messages count - step: " + answeredCounts);
  //       }
  //     } catch (error) {}
  //   };

  //   fetchData();
  //   setLoading(false);
  // }, [step]);

  const isClient =
    !checkPermission("Obsługa wniosków") &&
    !checkPermission("Obsługa wysyłki");
  const showOperatorRemarks =
    isClient &&
    Number(status) === 4 &&
    id !== "00000000-0000-0000-0000-000000000000";

  if (loading) {
    return (
      <div className="w-100 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (displayMode === 0)
    return (
      <AccessControl>
        <div className="d-flex flex-column w-100">
          <Stepper
            activeStep={step}
            warnings={warnings}
            messages={messages}
            answeredMessages={answeredMessages}
            id={id}
            titles={
              displayMode === 0
                ? applicationStepTitles
                : status === 8
                ? metadataStepTitlesCancelled
                : metadataStepTitles
            }
            applicationMode={displayMode === 0}
          />
          <div
            className="application-edit-content d-flex flex-row content p-0 w-100"
            ref={containerRef}
          >
            <div className="application-edit-layout position-relative w-100">
              <OperatorsPanel
                isVisible={status !== 1}
                modeChangeHandler={() => setShowWarning(true)}
              />

              {showOperatorRemarks && (
                <OperatorRemarksCard
                  remarks={operatorRemarks}
                  onRemarkClick={handleRemarkClick}
                />
              )}

              {step === "1" && (
                <ApplicationAdd
                  registerCallback={registerCallback}
                  warningsCallback={setWarningCount}
                  operator={operator}
                />
              )}
              {step === "2" && (
                <ApplicationStep2
                  registerCallback={registerCallback}
                  warningsCallback={setWarningCount}
                  operator={operator}
                />
              )}
              {step === "3" && (
                <ApplicationStep3
                  registerCallback={registerCallback}
                  warningsCallback={setWarningCount}
                  operator={operator}
                />
              )}
              {step === "4" && (
                <ApplicationStep4
                  registerCallback={registerCallback}
                  warningsCallback={setWarningCount}
                  operator={operator}
                />
              )}
              {step === "5" && (
                <ApplicationStep5
                  registerCallback={registerCallback}
                  warningsCallback={setWarningCount}
                  operator={operator}
                />
              )}

              {status !== 1 && (
                <div className="d-flex flex-row w-100 py-2 border-top border-1">
                  <button
                    className="btn btn-outline-primary w11 ms-4 me-auto"
                    onClick={() => setShowWarning(true)}
                  >
                    Przejdź do widoku sprawy
                  </button>
                </div>
              )}

              <ConfirmPopup
                show={showWarning}
                title="Uwaga"
                text="Czy na pewno chcesz opuścić widok edycji wniosku? Niezapisane zmiany zostaną utracone."
                confirmText="Tak"
                cancelText="Nie"
                onConfirm={() => {
                  handleDisplayModeChange(1);
                  setShowWarning(false);
                }}
                onCancel={() => setShowWarning(false)}
              />
            </div>
          </div>
        </div>
      </AccessControl>
    );

  if (displayMode === 1)
    return (
      <AccessControl>
        <div className="d-flex flex-column w-100">
          <Stepper
            activeStep={statusToActiveStepMap[status]}
            id={id}
            titles={
              displayMode === 0
                ? applicationStepTitles
                : status === 8
                ? metadataStepTitlesCancelled
                : metadataStepTitles
            }
            applicationMode={displayMode === 0}
          />
          <div className="d-flex flex-row content p-0 w-100">
            <div className="pb-4">
              <ApplicationMetadata
                displayModeHandler={handleDisplayModeChange}
              />
            </div>
          </div>
        </div>
      </AccessControl>
    );
}

export default ProcessApplication;


